import React from "react";
import type { TFunction } from "i18next";
import type { Schema } from "@/shared/components/Form/types";
import { ArrayItemRow } from "@/shared/components/Form";
import { LanguageAutocomplete } from "@/partner/entities/languages";

export function makePlatformSettingsSchema(
  t: TFunction,
  reportCurrency?: string | null
): Schema {
  return {
    title: {
      type: "text",
      label: t("platformSettings.form.titleLabel"),
      xs: 12,
      sm: 8,
      fullWidth: true,
      required: true,
    },
    languageId: {
      type: "fieldComponent",
      label: t("platformSettings.form.languageLabel"),
      xs: 12,
      sm: 4,
      fullWidth: true,
      fieldComponent: ({ onChange, value, label, editData, disabled }) =>
        React.createElement(LanguageAutocomplete, {
          onChange,
          value: (value as string | null) ?? null,
          label,
          initialLanguage: editData?.language ?? null,
          disabled: disabled as boolean | undefined,
        }),
    },
    description: {
      type: "text",
      label: t("platformSettings.form.descriptionLabel"),
      xs: 12,
      fullWidth: true,
      multiline: true,
      rows: 2,
    },
    _integrationsSectionTitle: {
      type: "title",
      label: t("platformSettings.form.integrationsSectionTitle"),
      helperText: t("platformSettings.form.integrationsSectionHelper"),
      xs: 12,
    },
    gaId: {
      type: "text",
      label: t("platformSettings.form.gaIdLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    googleSiteVerification: {
      type: "text",
      label: t("platformSettings.form.googleSiteVerificationLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    customHead: {
      type: "text",
      label: t("platformSettings.form.customHeadLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
      multiline: true,
      rows: 4,
    },
    customHTML: {
      type: "text",
      label: t("platformSettings.form.customHtmlLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
      multiline: true,
      rows: 4,
    },
    _footerSectionTitle: {
      type: "title",
      label: t("platformSettings.form.footerSectionTitle"),
      xs: 12,
    },
    "footer.title": {
      type: "text",
      label: t("platformSettings.form.footerTitleLabel"),
      helperText: t("platformSettings.form.placeholdersHelper"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    "footer.subtitle": {
      type: "text",
      label: t("platformSettings.form.footerSubtitleLabel"),
      helperText: t("platformSettings.form.placeholdersHelper"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    _termsAndPolicies: {
      type: "title",
      label: t("platformSettings.form.termsSectionTitle"),
      xs: 12,
    },
    acceptTermsOfServiceOnSignIn: {
      type: "switch",
      label: t("platformSettings.form.acceptTermsOfServiceOnSignInLabel"),
      helperText: t("platformSettings.form.acceptTermsOfServiceOnSignInHelper"),
      xs: 12,
      fullWidth: true,
    },
    privacyPolicyUrl: {
      type: "text",
      label: t("platformSettings.form.privacyPolicyUrlLabel"),
      helperText: t("platformSettings.form.privacyPolicyUrlHelper"),
      xs: 12,
      sm: 4,
      fullWidth: true,
    },
    termsOfServiceUrl: {
      type: "text",
      label: t("platformSettings.form.termsOfServiceUrlLabel"),
      helperText: t("platformSettings.form.termsOfServiceUrlHelper"),
      xs: 12,
      sm: 4,
      fullWidth: true,
    },
    refundPolicyUrl: {
      type: "text",
      label: t("platformSettings.form.refundPolicyUrlLabel"),
      helperText: t("platformSettings.form.refundPolicyUrlHelper"),
      xs: 12,
      sm: 4,
      fullWidth: true,
    },
    _legalEntitySectionTitle: {
      type: "title",
      label: t("platformSettings.form.legalEntitySectionTitle"),
      helperText: t("platformSettings.form.legalEntityHelper"),
      xs: 12,
    },
    // Валюта отчётов организации: read-only подсказка (валюту меняет
    // администратор, см. Organization.currency), рендерится только когда
    // текущая валюта известна (currentOrganization ещё не загружен на
    // первый рендер PlatformSettings).
    ...(reportCurrency
      ? {
          _reportCurrencySectionTitle: {
            type: "title" as const,
            variant: "body2" as const,
            helperText: t("platformSettings.form.reportCurrencyHint", {
              currency: reportCurrency,
            }),
            xs: 12,
          },
        }
      : {}),
    "legalEntity.name": {
      type: "text",
      label: t("platformSettings.form.legalEntityNameLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    "legalEntity.address": {
      type: "text",
      label: t("platformSettings.form.legalEntityAddressLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    "legalEntity.registrationNumber": {
      type: "text",
      label: t("platformSettings.form.legalEntityRegistrationNumberLabel"),
      helperText: t(
        "platformSettings.form.legalEntityRegistrationNumberHelper"
      ),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    "legalEntity.email": {
      type: "email",
      label: t("platformSettings.form.legalEntityEmailLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    "legalEntity.phone": {
      type: "text",
      label: t("platformSettings.form.legalEntityPhoneLabel"),
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    customTerms: {
      type: "array",
      label: t("platformSettings.form.customTermsLabel"),
      addText: t("platformSettings.form.addCustomTerm"),
      xs: 12,
      itemComponent: ArrayItemRow,
      subschema: {
        label: {
          label: t("platformSettings.form.customTermLabelLabel"),
          type: "text",
          xs: 12,
          sm: 6,
          fullWidth: true,
          required: true,
        },
        url: {
          label: t("platformSettings.form.customTermUrlLabel"),
          type: "text",
          xs: 12,
          sm: 6,
          fullWidth: true,
          required: true,
        },
      },
    },
  };
}
